import { Button, Card, FormFieldGroup, FormLabel, FormPriceInput, FormSelect, Text } from '$components'
import { DurationPicker } from '$components'
import { ElementIntent } from '$components'
import { BinIcon } from '$components'
import { useState } from 'react'
import { type PricingVariantSchema } from '../forms/shared/pricing-variant-schema'

type SelectOption = {
  id: string
  value: string
}

export type PricingVariantFormValue = PricingVariantSchema

type PricingVariantCardProps = {
  baseFieldName: string
  index: number
  variant: PricingVariantFormValue
  currency?: string
  sizeOptions: SelectOption[]
  coatOptions: SelectOption[]
  breedOptions: SelectOption[]
  overrideType?: 'breed' | 'size-coat' | null
  onDurationChange: (index: number, duration: number) => void
  onRemove?: (index: number) => void
  canRemove?: boolean
}

export function PricingVariantCard({
  baseFieldName,
  index,
  variant,
  currency,
  sizeOptions,
  coatOptions,
  breedOptions,
  overrideType,
  onDurationChange,
  onRemove,
  canRemove = false,
}: PricingVariantCardProps) {
  const [breedSearch, setBreedSearch] = useState('')
  const isDefault = variant.is_default
  const isBreedOverride = !isDefault && (!!variant.breed || overrideType === 'breed')
  const sizeFieldName = `${baseFieldName}.${index}.size_category`
  const coatFieldName = `${baseFieldName}.${index}.coat_type`
  const breedFieldName = `${baseFieldName}.${index}.breed`
  const priceFieldName = `${baseFieldName}.${index}.price`
  const durationFieldName = `${baseFieldName}.${index}.duration`

  return (
    <Card
      rounding="xl"
      padding="sm"
    >
      <div className="flex items-start justify-between mb-stack-roomy">
        <div className="flex flex-col gap-stack-dense">
          <Text
            type="s"
            weight="semibold"
            className="text-fg"
          >
            {isDefault
              ? 'Base pricing'
              : isBreedOverride
                ? 'Adjust by breed'
                : 'Adjust by size & coat'}
          </Text>

          {isDefault
            ? (
              <Text
                type="xs"
                className="text-fg-subtle"
              >
                Applies to all breeds unless you add pricing for specific sizes, coats, or breeds.
              </Text>
            )
            : isBreedOverride
              ? (
                <Text
                  type="xs"
                  className="text-fg-subtle"
                >
                  Applies only to this breed. This pricing takes priority over size and coat pricing.
                </Text>
              )
              : (
                <Text
                  type="xs"
                  className="text-fg-subtle"
                >
                  Applies only when the breed matches this size and coat combination.
                </Text>
              )}
        </div>

        {canRemove && onRemove && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            icon={BinIcon}
            onClick={() => onRemove(index)}
            intent={ElementIntent.Danger}
            aria-label="Remove pricing"
          />
        )}
      </div>

      {!isDefault && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-stack-roomy mb-stack-roomy">
          {isBreedOverride
            ? (
              <FormFieldGroup className="sm:col-span-2">
                <FormLabel htmlFor={breedFieldName}>
                  Breed
                </FormLabel>

                <FormSelect
                  name={breedFieldName}
                  placeholder="Select breed..."
                  options={breedOptions}
                  search={{
                    search: breedSearch,
                    onSearchChange: setBreedSearch,
                    searchPlaceholder: 'Search breeds...',
                  }}
                />
              </FormFieldGroup>
            )
            : (
              <>
                <FormFieldGroup>
                  <FormLabel htmlFor={sizeFieldName}>
                    Size Category
                  </FormLabel>

                  <FormSelect
                    name={sizeFieldName}
                    placeholder="Any size..."
                    options={sizeOptions}
                  />
                </FormFieldGroup>

                <FormFieldGroup>
                  <FormLabel htmlFor={coatFieldName}>
                    Coat Type
                  </FormLabel>

                  <FormSelect
                    name={coatFieldName}
                    placeholder="Any coat type..."
                    options={coatOptions}
                  />
                </FormFieldGroup>
              </>
            )}
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-stack-roomy">
        <FormFieldGroup>
          <FormLabel htmlFor={priceFieldName}>
            Price
          </FormLabel>

          <FormPriceInput
            name={priceFieldName}
            currency={currency}
            min={0}
            placeholder="0.00"
          />
        </FormFieldGroup>

        <FormFieldGroup>
          <FormLabel htmlFor={durationFieldName}>
            Duration
          </FormLabel>

          <DurationPicker
            duration={variant.duration || 30}
            onChange={(duration) => onDurationChange(index, duration)}
          />
        </FormFieldGroup>
      </div>
    </Card>
  )
}
